/**
 * Öffnet das Cookie-Einstellungen-Fenster über Links, die dem Seitenbetreiber
 * gehören - Menüpunkte, Page-Builder-Buttons, Theme-Templates, Textlinks.
 * Dort ist kein Shortcode möglich, und der eigene Button der Shortcodes passt
 * sich nicht in fremdes Markup ein.
 *
 * Dokumentierte, stabile Wege (siehe readme.txt, "Cookie-Einstellungen über
 * einen eigenen Link öffnen"):
 *
 * - Anker:      <a href="#pixelmate-settings">Cookie-Einstellungen</a>
 *               (auch als vollständige URL, z. B. https://example.com/#pixelmate-settings)
 * - CSS-Klasse: .pixelmate-open-settings am Element oder einem Vorfahren
 *               (WordPress setzt Menü-Klassen ans <li>, nicht ans <a>)
 * - Aufruf:     ?pixelmate=settings bzw. #pixelmate-settings in der Adresse
 *               öffnet das Fenster direkt nach dem Laden
 * - JavaScript: window.Pixelmate.openSettings()
 *
 * Der Banner wird über eine Funktion geholt, weil pixelmateReinit() die
 * Instanz austauscht.
 */
import { pmError } from './Logger.js';

export class SettingsLinks {
    static HASH = '#pixelmate-settings';
    static CLASS_NAME = 'pixelmate-open-settings';
    static QUERY_PARAM = 'pixelmate';
    static QUERY_VALUE = 'settings';

    /**
     * @param {() => (object|null|undefined)} getBanner Liefert den aktuellen PixelmateBanner.
     */
    constructor(getBanner) {
        this.getBanner = getBanner;
        this.handleClick = this.handleClick.bind(this);
        this.handleHashChange = this.handleHashChange.bind(this);
    }

    init() {
        // Capture-Phase: Themes mit Smooth-Scroll oder Mobilmenüs fangen
        // Klicks auf "#…"-Links sonst vorher ab.
        document.addEventListener('click', this.handleClick, true);
        window.addEventListener('hashchange', this.handleHashChange);

        this.openFromLocation();

        return this;
    }

    destroy() {
        document.removeEventListener('click', this.handleClick, true);
        window.removeEventListener('hashchange', this.handleHashChange);
    }

    /**
     * Ob ein Element ein Einstellungs-Link ist. Liefert das auslösende
     * Element oder null.
     */
    findTrigger(target) {
        if (!target || typeof target.closest !== 'function') return null;

        const byClass = target.closest(`.${SettingsLinks.CLASS_NAME}`);
        if (byClass) return byClass;

        const link = target.closest('a[href]');
        if (link && this.isSettingsHref(link.getAttribute('href'))) return link;

        return null;
    }

    /**
     * "#pixelmate-settings" oder eine URL mit diesem Fragment. Die Seite
     * dahinter spielt keine Rolle: Das Fenster gibt es auf jeder Seite, ein
     * Seitenwechsel nur zum Öffnen wäre überflüssig.
     */
    isSettingsHref(href) {
        if (!href) return false;
        const hashIndex = href.indexOf('#');
        if (hashIndex === -1) return false;

        return href.slice(hashIndex).toLowerCase() === SettingsLinks.HASH;
    }

    handleClick(event) {
        // Mittlere Maustaste/Strg-Klick bleiben dem Browser überlassen.
        if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const trigger = this.findTrigger(event.target);
        if (!trigger) return;

        event.preventDefault();
        this.openSettings();
    }

    handleHashChange() {
        if ((window.location.hash || '').toLowerCase() === SettingsLinks.HASH) {
            this.openSettings();
            this.cleanLocation();
        }
    }

    /**
     * Aufruf von einer anderen Seite (Datenschutzerklärung, E-Mail):
     * ?pixelmate=settings oder #pixelmate-settings in der Adresse.
     */
    openFromLocation() {
        if (!this.locationRequestsSettings()) return false;

        this.openSettings();
        this.cleanLocation();

        return true;
    }

    locationRequestsSettings() {
        const { hash, search } = window.location;
        if ((hash || '').toLowerCase() === SettingsLinks.HASH) return true;

        try {
            return new URLSearchParams(search || '').get(SettingsLinks.QUERY_PARAM) === SettingsLinks.QUERY_VALUE;
        } catch (e) {
            return false;
        }
    }

    /**
     * Entfernt Parameter und Anker aus der Adresse, damit ein Reload (z. B.
     * nach der Einwilligung bei aktiver CSP) das Fenster nicht erneut öffnet
     * und derselbe Link ein zweites Mal ein hashchange auslöst.
     */
    cleanLocation() {
        if (!window.history || typeof window.history.replaceState !== 'function') return;

        try {
            const url = new URL(window.location.href);
            if (url.searchParams.get(SettingsLinks.QUERY_PARAM) === SettingsLinks.QUERY_VALUE) {
                url.searchParams.delete(SettingsLinks.QUERY_PARAM);
            }
            if (url.hash.toLowerCase() === SettingsLinks.HASH) {
                url.hash = '';
            }
            window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        } catch (e) {
            // Ohne bereinigte Adresse funktioniert der Link trotzdem.
        }
    }

    openSettings() {
        const banner = this.getBanner();
        if (!banner || typeof banner.openSettings !== 'function') {
            pmError('Pixelmate settings link: banner not initialized');
            return false;
        }

        banner.openSettings();
        return true;
    }

    /**
     * Öffentliche, dokumentierte Schnittstelle unter window.Pixelmate.
     * Integrationen rufen nur diese Funktionen auf, nie die interne
     * Banner-Instanz.
     */
    publicApi() {
        return Object.freeze({
            openSettings: () => this.openSettings()
        });
    }
}
